import type { GasketWorkflowStatus, MapareRow, MedocCandidate, ReviewResolution } from '@lyra/shared';
import { rowNeedsManualReview } from './rows';

/**
 * Pure classify-response normalizer + retry/edit predicates for the Mapări
 * review/correction loop (Plan 3c-2 Task 1). Faithful Rule-Zero port of:
 *   - `app/src/services/gasketApi.ts` (`normalizeClassification` +
 *     `hasDirectClassificationPayload` + `normalizeCandidate`) — the dual
 *     legacy/direct envelope parsing and the snake_case wire payload.
 *   - `app/src/hooks/useClassification.ts`'s `retryErrorRows` (~L579-619) —
 *     the snake_case → camelCase mapping actually applied to a classify()
 *     response when building row fields (primaryCandidate/alternateCandidates).
 *   - `app/src/hooks/useClassification.ts`'s `rowsNeedingRetry` (~L550-564).
 *   - `app/src/components/ResultsTable.tsx`'s `handleEditSave` (~L201-221),
 *     made canonical over the divergent `ReviewQueueView.ManualMapSection`
 *     edit path per the Plan 3c-2 spec's decision D2.
 */

/**
 * CamelCase shape of one MEDOC candidate. Reuses `@lyra/shared`'s
 * `MedocCandidate` — the same type already used by
 * `MapareRow.primaryCandidate`/`alternateCandidates` — rather than
 * duplicating an identical local interface.
 */

/** Return shape of `normalizeClassifyResponse` — the camelCase classification the app consumes. */
export interface NormalizedClassification {
  codMedoc: string;
  denumireMedoc: string;
  codBdMedoc?: string;
  confidence: number;
  reasoning: string;
  workflowStatus: GasketWorkflowStatus;
  primaryCandidate?: MedocCandidate;
  alternateCandidates?: MedocCandidate[];
}

/** One raw MEDOC candidate as the orchestrator sends it (snake_case), with a defensive camelCase fallback. */
interface RawCandidate {
  cod_medoc?: string;
  denumire_medoc?: string;
  cod_bd_medoc?: string;
  confidence?: number;
  reasoning?: string;
  source?: string;
  codMedoc?: string;
  denumireMedoc?: string;
  codBdMedoc?: string;
}

/**
 * Raw `POST /classify` response body. Ported field-for-field from
 * gasketApi.ts's `ClassifyResponseRaw` (voting/duration_ms omitted — those
 * are consumed by `GasketResponseFull`, outside this normalizer's scope).
 */
export interface ClassifyResponseRaw {
  result?: string;
  cod_medoc?: string;
  denumire_medoc?: string;
  cod_bd_medoc?: string;
  confidence?: number;
  reasoning?: string;
  status?: string;
  is_ambiguous?: boolean;
  needs_review?: boolean;
  source?: string;
  resolution_strategy?: string;
  ambiguity_reason?: string;
  primary_candidate?: RawCandidate;
  alternate_candidates?: RawCandidate[];
  partner_code?: string;
  request_id?: string;
}

const WORKFLOW_STATUSES = new Set<string>([
  'resolved',
  'ambiguous',
  'needs_review',
  'no_match',
  'low_confidence',
]);

/**
 * Ported verbatim from gasketApi.ts's private `hasDirectClassificationPayload`.
 * Decides whether a response with no `result` envelope still carries a valid
 * direct classification payload (top-level resolved fields, or a recognized
 * `status` — except a bare "resolved" with no resolved fields, which is
 * treated as invalid).
 */
function hasDirectClassificationPayload(raw: ClassifyResponseRaw): boolean {
  const hasPrimaryCandidate =
    raw.primary_candidate != null &&
    (raw.primary_candidate.cod_medoc != null ||
      raw.primary_candidate.denumire_medoc != null ||
      raw.primary_candidate.codMedoc != null ||
      raw.primary_candidate.denumireMedoc != null);
  const hasResolvedFields = Boolean(raw.cod_medoc || raw.denumire_medoc || hasPrimaryCandidate);
  const hasWorkflowStatus = typeof raw.status === 'string' && WORKFLOW_STATUSES.has(raw.status);

  if (!hasResolvedFields && !hasWorkflowStatus) {
    return false;
  }

  if (raw.status === 'resolved' && !hasResolvedFields) {
    return false;
  }

  return true;
}

/**
 * Ported verbatim from gasketApi.ts's private `normalizeCandidate`. Used only
 * to backfill the top-level cod_medoc/denumire_medoc/cod_bd_medoc fields from
 * a supplied primary_candidate — mirrors the dual snake_case/camelCase key
 * acceptance of the original (defensive against either input shape).
 */
function normalizeCandidateForBackfill(candidate: RawCandidate | undefined): MedocCandidate | undefined {
  if (!candidate) return undefined;
  return {
    codMedoc: String(candidate.cod_medoc ?? candidate.codMedoc ?? ''),
    denumireMedoc: String(candidate.denumire_medoc ?? candidate.denumireMedoc ?? ''),
    codBdMedoc:
      candidate.cod_bd_medoc != null || candidate.codBdMedoc != null
        ? String(candidate.cod_bd_medoc ?? candidate.codBdMedoc)
        : undefined,
    confidence: typeof candidate.confidence === 'number' ? candidate.confidence : undefined,
    reasoning: typeof candidate.reasoning === 'string' ? candidate.reasoning : undefined,
    source: typeof candidate.source === 'string' ? candidate.source : undefined,
  };
}

/**
 * Snake_case → camelCase candidate mapping, matching
 * `useClassification.ts`'s `retryErrorRows` (~L579-588 primary, ~L610-619
 * alternates) EXACTLY — snake_case fields only, no camelCase fallback (that
 * fallback is `normalizeCandidateForBackfill`'s job, a separate v1 step).
 */
function mapCandidateToCamel(candidate: RawCandidate): MedocCandidate {
  return {
    codMedoc: candidate.cod_medoc ?? '',
    denumireMedoc: candidate.denumire_medoc ?? '',
    codBdMedoc: candidate.cod_bd_medoc,
    confidence: candidate.confidence,
    reasoning: candidate.reasoning,
    source: candidate.source,
  };
}

/**
 * Normalizes a raw `POST /classify` response into the camelCase shape the
 * app consumes. Faithful port of gasketApi.ts's `normalizeClassification`:
 *   1. Dual envelope: `raw.result` is a JSON string (legacy) to `JSON.parse`,
 *      OR the fields are top-level (direct envelope).
 *   2. Validity guard: throws the same Romanian error v1 throws when neither
 *      a `result` string nor a direct payload (per `hasDirectClassificationPayload`)
 *      is present.
 *   3. Defaulting: missing `status` → 'resolved'; synthesize `primary_candidate`
 *      from top-level fields when absent; reset a malformed (non-array)
 *      `alternate_candidates` to `[]`; backfill top-level cod_medoc/denumire_medoc/
 *      cod_bd_medoc from the (possibly-synthesized) primary candidate.
 *   4. Field mapping to camelCase (cod_medoc→codMedoc, denumire_medoc→denumireMedoc,
 *      cod_bd_medoc→codBdMedoc, status→workflowStatus, confidence/reasoning as-is,
 *      candidate arrays mapped snake→camel).
 */
export function normalizeClassifyResponse(raw: ClassifyResponseRaw): NormalizedClassification {
  if (!raw.result && !hasDirectClassificationPayload(raw)) {
    throw new Error('Răspuns invalid de la serviciul de clasificare');
  }

  const payload: ClassifyResponseRaw = raw.result
    ? (JSON.parse(raw.result) as ClassifyResponseRaw)
    : {
        cod_medoc: raw.cod_medoc ?? '',
        denumire_medoc: raw.denumire_medoc ?? '',
        cod_bd_medoc: raw.cod_bd_medoc,
        confidence: raw.confidence ?? 0,
        reasoning: raw.reasoning ?? '',
        status: raw.status,
        is_ambiguous: raw.is_ambiguous,
        needs_review: raw.needs_review,
        source: raw.source,
        resolution_strategy: raw.resolution_strategy,
        ambiguity_reason: raw.ambiguity_reason,
        primary_candidate: raw.primary_candidate,
        alternate_candidates: raw.alternate_candidates,
        partner_code: raw.partner_code,
        request_id: raw.request_id,
      };

  if (!payload.status) {
    payload.status = 'resolved';
  }

  if (!payload.primary_candidate && (payload.cod_medoc || payload.denumire_medoc)) {
    payload.primary_candidate = {
      cod_medoc: payload.cod_medoc,
      denumire_medoc: payload.denumire_medoc,
      cod_bd_medoc: payload.cod_bd_medoc,
      confidence: payload.confidence,
      reasoning: payload.reasoning,
      source: payload.source,
    };
  }

  if (payload.alternate_candidates && !Array.isArray(payload.alternate_candidates)) {
    payload.alternate_candidates = [];
  }

  const primary = normalizeCandidateForBackfill(payload.primary_candidate);
  if (primary && !payload.cod_medoc) payload.cod_medoc = primary.codMedoc;
  if (primary && !payload.denumire_medoc) payload.denumire_medoc = primary.denumireMedoc;
  if (primary && !payload.cod_bd_medoc) payload.cod_bd_medoc = primary.codBdMedoc;

  return {
    codMedoc: payload.cod_medoc ?? '',
    denumireMedoc: payload.denumire_medoc ?? '',
    codBdMedoc: payload.cod_bd_medoc,
    confidence: payload.confidence ?? 0,
    reasoning: payload.reasoning ?? '',
    workflowStatus: payload.status as GasketWorkflowStatus,
    primaryCandidate: payload.primary_candidate ? mapCandidateToCamel(payload.primary_candidate) : undefined,
    alternateCandidates: payload.alternate_candidates ? payload.alternate_candidates.map(mapCandidateToCamel) : undefined,
  };
}

/**
 * Ported verbatim from `useClassification.ts`'s `rowsNeedingRetry`
 * (~L550-564): candidates for retry are rows that either errored outright,
 * or came back "classified" with no signal at all (no workflowStatus, no
 * reviewResolution, zero confidence, and missing codMedoc/denumireMedoc) —
 * excluding rows still `pending` (never attempted) and any row that already
 * carries a workflowStatus or reviewResolution (handled by manual review,
 * not retry).
 */
export function rowsNeedingRetry(rows: MapareRow[]): MapareRow[] {
  return rows.filter(
    (r) =>
      r.status === 'error' ||
      (r.status !== 'pending' &&
        !r.workflowStatus &&
        r.reviewResolution == null &&
        r.confidence === 0 &&
        (!r.codMedoc || !r.denumireMedoc))
  );
}

/** The fields an operator picks when correcting a row's classification (nomenclator search result or manual entry). */
export interface EditPick {
  codMedoc: string;
  denumireMedoc: string;
  codBdMedoc?: string;
}

/**
 * Canonical single-row edit payload (Plan 3c-2 spec §9 decision D2). Ported
 * from `ResultsTable.tsx`'s `handleEditSave` (~L209-218), made canonical over
 * the divergent, bookkeeping-less `ReviewQueueView.ManualMapSection` edit
 * path: always sets `codMedoc`/`denumireMedoc`/`codBdMedoc` (falling back to
 * the row's existing `codBdMedoc` when the pick omits it), `status:'edited'`,
 * and — matching v1 exactly — UNCONDITIONALLY stashes the row's pre-overwrite
 * `codMedoc`/`denumireMedoc` as `originalCodMedoc`/`originalDenumireMedoc`
 * (shown as the amber "Clasificare originală" audit note, regardless of
 * whether the row needed review — e.g. a `resolved` row corrected via
 * nomenclator search per spec §5 still gets this note). Only
 * `reviewResolution:'selected_alternate'` is gated on the row having
 * previously needed manual review (`rowNeedsManualReview`); v1's `needsReview`
 * reset is dropped as vestigial (unused by the ported `rowNeedsManualReview`
 * predicate).
 */
export function buildEditPayload(row: MapareRow, picked: EditPick): Partial<MapareRow> {
  const patch: Partial<MapareRow> = {
    codMedoc: picked.codMedoc,
    denumireMedoc: picked.denumireMedoc,
    codBdMedoc: picked.codBdMedoc ?? row.codBdMedoc,
    status: 'edited',
    originalCodMedoc: row.codMedoc,
    originalDenumireMedoc: row.denumireMedoc,
  };

  if (rowNeedsManualReview(row)) {
    patch.reviewResolution = 'selected_alternate' as ReviewResolution;
  }

  return patch;
}
